import { Input, Select, Text } from "@medusajs/ui"
import { useMemo } from "react"
import { Field } from "./form"
import { addressFormat, checkAddress, countryOptions, formatAddressLines, normalizePostal, type AddressInput } from "@/lib/geo"

export type AddressValue = {
  first_name?: string; last_name?: string; company?: string
  address_1?: string; address_2?: string; city?: string
  province?: string; postal_code?: string; country_code?: string; phone?: string
}

/**
 * One address form for the whole admin, laid out like Shopify's and driven by the
 * country's format (lib/geo): State/Province/County/… label, a list when the
 * country has one, ZIP/Postcode/PIN label, no postal field where countries have none.
 */
export function AddressFields({
  value,
  onChange,
  countries,
  name = true,
  company = true,
  phone = true,
  disabled,
  compact,
}: {
  value: AddressValue
  onChange: (v: AddressValue) => void
  /** Limit the country list (lowercase codes), e.g. to the countries the store sells to. */
  countries?: string[]
  name?: boolean
  company?: boolean
  phone?: boolean
  disabled?: boolean
  /** Narrow column (side cards): two columns, city on its own row. */
  compact?: boolean
}) {
  const country = (value.country_code || "us").toLowerCase()
  const fmt = addressFormat(country)
  const options = useMemo(() => {
    const list = countryOptions(countries)
    // Keep a saved country selectable even if it's outside the allowed list.
    return list.some((c) => c.code === country) ? list : [...list, ...countryOptions([country])]
  }, [countries, country])
  const set = (k: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value })
  const setCountry = (c: string) => {
    const next = addressFormat(c)
    const keep = !value.province || (next.provinces ? next.provinces.some((p) => p.code === value.province?.toUpperCase()) : !fmt.provinces)
    onChange({ ...value, country_code: c, province: keep ? value.province : "" })
  }
  const span = compact
    ? { full: "col-span-2", half: "col-span-1", city: "col-span-2", prov: fmt.postal === "none" ? "col-span-2" : "col-span-1", postal: "col-span-1" }
    : { full: "sm:col-span-6", half: "sm:col-span-3", city: fmt.postal === "none" ? "sm:col-span-3" : "sm:col-span-2", prov: fmt.postal === "none" ? "sm:col-span-3" : "sm:col-span-2", postal: "sm:col-span-2" }
  return (
    <div className={compact ? "grid grid-cols-2 gap-2" : "grid gap-3 sm:grid-cols-6"}>
      <div className={span.full}>
        <Field label="Country/region">
          <Select value={country} onValueChange={setCountry} disabled={disabled}>
            <Select.Trigger><Select.Value placeholder="Country/region" /></Select.Trigger>
            <Select.Content>{options.map((c) => <Select.Item key={c.code} value={c.code}>{c.name}</Select.Item>)}</Select.Content>
          </Select>
        </Field>
      </div>
      {name && (
        <>
          <div className={span.half}><Field label="First name"><Input value={value.first_name ?? ""} onChange={set("first_name")} disabled={disabled} /></Field></div>
          <div className={span.half}><Field label="Last name"><Input value={value.last_name ?? ""} onChange={set("last_name")} disabled={disabled} /></Field></div>
        </>
      )}
      {company && <div className={span.full}><Field label="Company"><Input value={value.company ?? ""} onChange={set("company")} disabled={disabled} /></Field></div>}
      <div className={span.full}><Field label="Address"><Input value={value.address_1 ?? ""} onChange={set("address_1")} disabled={disabled} /></Field></div>
      <div className={span.full}><Field label="Apartment, suite, etc"><Input value={value.address_2 ?? ""} onChange={set("address_2")} disabled={disabled} /></Field></div>
      <div className={span.city}>
        <Field label="City"><Input value={value.city ?? ""} onChange={set("city")} disabled={disabled} /></Field>
      </div>
      <div className={span.prov}>
        <Field label={fmt.province === "optional" ? `${fmt.provinceLabel} (optional)` : fmt.provinceLabel}>
          {fmt.provinces ? (
            <Select value={value.province?.toUpperCase() || undefined} onValueChange={(v) => onChange({ ...value, province: v })} disabled={disabled}>
              <Select.Trigger><Select.Value placeholder={fmt.provinceLabel} /></Select.Trigger>
              <Select.Content>{fmt.provinces.map((p) => <Select.Item key={p.code} value={p.code}>{p.name}</Select.Item>)}</Select.Content>
            </Select>
          ) : (
            <Input value={value.province ?? ""} onChange={set("province")} disabled={disabled} />
          )}
        </Field>
      </div>
      {fmt.postal !== "none" && (
        <div className={span.postal}>
          <Field label={fmt.postal === "optional" ? `${fmt.postalLabel} (optional)` : fmt.postalLabel}>
            <Input value={value.postal_code ?? ""} onChange={set("postal_code")} onBlur={() => onChange({ ...value, postal_code: normalizePostal(value.postal_code) })} disabled={disabled} />
          </Field>
        </div>
      )}
      {phone && <div className={span.full}><Field label="Phone"><Input type="tel" value={value.phone ?? ""} onChange={set("phone")} disabled={disabled} /></Field></div>}
    </div>
  )
}

/** Problem with the address for its country (null = fine). Only looks at country/province/postal. */
export const addressProblem = (a: AddressValue, allowed?: string[]) => checkAddress(a as AddressInput, allowed)

/** Read-only address, one line per row, in the country's order. */
export function AddressText({ address, className, phone = true, name = true }: { address: AddressInput | null | undefined; className?: string; phone?: boolean; name?: boolean }) {
  const lines = formatAddressLines(address, { phone, name })
  if (!lines.length) return <Text size="small" className={"text-ui-fg-muted " + (className ?? "")}>No address</Text>
  return <Text size="small" className={"whitespace-pre-line " + (className ?? "")}>{lines.join("\n")}</Text>
}

/** One-line address for lists. */
export const addressLine = (a: AddressInput | null | undefined) => formatAddressLines(a, { name: false, phone: false }).join(", ")
